import React, { VFC } from "react";
import styled from "styled-components";
import LineButton from "~/components/atoms/ShareButton/LineButton";
import TwitterButton from "~/components/atoms/ShareButton/TwitterButton";
import AmpHelpfulButtonWithScript from "~/components/modules/qaActions/AmpHelpfulButtonWithScript";
import HelpfulButton from "~/components/modules/qaActions/HelpfulButton";
import { composeShareUrlLine, composeShareUrlTwitter } from "~/lib/share";
import {
  generateQaDetailPath,
  getQaHostAndLocaleForURL,
} from "~/lib/utils/urlHelper";
import { AboutInfo } from "~/models/aboutInfo";
import { Project } from "~/models/project";
import {
  calculateHelpfulAndUserReactionCount,
  QuestionAnswer,
} from "~/models/questionAnswer";
import { Topic } from "~/models/topic";

interface Props {
  aboutInfo?: AboutInfo;
  increaseFeedbackHelpfulCallback?: () => void;
  increaseFeedbackHelpfulUrl?: string;
  isAmp?: boolean;
  project: Project;
  questionAnswer: QuestionAnswer;
  topic: Topic;
}

const ShareAction: VFC<Props> = ({
  aboutInfo,
  increaseFeedbackHelpfulCallback,
  increaseFeedbackHelpfulUrl,
  isAmp,
  project,
  questionAnswer,
  topic,
}) => {
  const { host, locale } = getQaHostAndLocaleForURL({
    isAbsolute: true,
    project,
    questionAnswer,
  });

  const url = {
    ...generateQaDetailPath({ questionAnswer, topicSlug: topic.slug }),
    host,
  };

  const twitterShareUrl = aboutInfo?.twitterAccount
    ? composeShareUrlTwitter({
        account: aboutInfo.twitterAccount,
        hashTags: topic.twitterTags,
        locale,
        shareText: questionAnswer.questionContent ?? "",
        url,
      })
    : undefined;

  const lineShareUrl = composeShareUrlLine({
    locale,
    shareText: questionAnswer.questionContent ?? "",
    url,
  });

  return (
    <>
      {isAmp ? (
        <AmpHelpfulButtonWrapper>
          {increaseFeedbackHelpfulUrl && (
            <AmpHelpfulButtonWithScript
              buttonSize="medium"
              questionAnswerId={questionAnswer.id}
              helpfulCount={calculateHelpfulAndUserReactionCount(
                questionAnswer
              )}
              increaseFeedbackHelpfulUrl={increaseFeedbackHelpfulUrl}
            />
          )}
        </AmpHelpfulButtonWrapper>
      ) : (
        <HelpfulButton
          questionAnswerId={questionAnswer.id}
          topicId={topic.id}
          increaseFeedbackHelpfulCallback={increaseFeedbackHelpfulCallback}
          buttonSize="medium"
          helpfulCount={calculateHelpfulAndUserReactionCount(questionAnswer)}
        />
      )}
      {twitterShareUrl && (
        <TwitterButton twitterShareUrl={twitterShareUrl} size="medium" />
      )}
      <LineButton lineShareUrl={lineShareUrl} size="medium" />
    </>
  );
};

// in order to show shadow and due to regulations in amp-script,
// we need to adjust position for amp helpful button
const AmpHelpfulButtonWrapper = styled.div`
  left: 0;
  height: 70px;
  position: relative;
  top: 0;
`;

export default ShareAction;
